// docs/93 AQ10 — a figure where it meets the water, by its gait (never its kit). Lives inside the
// unit's container like the status looks (docs/69), so it walks, lunges and sorts with it, and is
// driven from the scene's update (playback tweens never touch it, and `setSnapshot` killing every
// tween leaves it be):
//
// - in the shallows (or a strider in the deep) the figure is cut at a waterline: the body sinks into
//   the square by the hidden share (the image's crop and origin, which no tween moves) with a ripple
//   ring at the line;
// - a submerged swimmer shows only its back, dark under the surface, over a dark shadow, rings
//   spreading; it rises when it surfaces (the strike) and slips back down when it dives;
// - a floundering figure bobs low in the deep (the status look thrashes and bubbles);
// - a flier standing hovers a little over its ground shadow.
//
// The square it is on is read from where the container stands, so the line moves as the figure
// crosses the halfway of each step of a walk or a shove; going in or out, under or up, it tells the
// board's `WaterFx` (foam, spray, the plunge's big splash) and it leaves a wake as it goes.
import Phaser from 'phaser';
import { CUT, bodyWater, flies, waterLook, waterMoment, wakeStep, type GaitKey, type WaterLook } from '../../core/sim/water';
import type { StatusKind } from '../../core/sim/types';
import { waterFxOf } from './WaterFx';

export interface WaterLineHost {
  readonly scene: Phaser.Scene;
  readonly container: Phaser.GameObjects.Container;
  /** The body and its outline copies (each is cut the same). */
  bodies(): Phaser.GameObjects.Image[];
  readonly body: Phaser.GameObjects.Image;
  readonly cell: number;
  readonly size: number;
  /** The image origin's y at rest (`bodyOriginY`), read live: a hero's kit look swaps its texture (docs/145 KH6). */
  originY(): number;
  readonly gait: GaitKey;
  /** The statuses the figure shows now (its aura's). */
  statuses(): readonly StatusKind[];
  /** Board px it is off the ground in a flight (docs/102). */
  lift(): number;
  /** It fell: the water leaves it alone. */
  fallen(): boolean;
}

/** Share of the figure per ms the waterline moves toward its pose (≈ a quarter second end to end). */
const EASE_PER_MS = 1 / 260;
const VEIL = 0x123a56;
const RIPPLE = 0xdff1ff;
const SHADOW = 0x06141f;

/** A flier's hover over the ground while it stands, in squares (docs/93 AQ10). */
export const HOVER = 0.16;

export class WaterLine {
  private look: WaterLook = { pose: 'dry', cut: 0 };
  private level = 0;
  private hoverK = 0;
  private sink = 0;
  private applied = '';
  private first = true;
  private lastWake?: { x: number; y: number };
  private lastT = 0;
  private dead = false;
  /** Forced sinking (a withdrawal): 0..1 more of the figure under, and gone at 1. */
  private sinking?: { from: number; ms: number; born: number };
  private readonly back: Phaser.GameObjects.Graphics;
  private readonly front: Phaser.GameObjects.Graphics;
  private readonly veil: Phaser.GameObjects.Image;
  private readonly onUpdate = (t: number) => this.update(t);

  constructor(private readonly host: WaterLineHost) {
    const { scene, container } = host;
    this.back = scene.add.graphics();
    this.front = scene.add.graphics();
    container.addAt(this.back, 1);
    const b = host.body;
    this.veil = scene.add.image(0, 0, b.texture.key, b.frame.name).setOrigin(0.5, host.originY()).setScale(b.scaleX, b.scaleY).setTint(VEIL).setTintMode(Phaser.TintModes.FILL).setAlpha(0).setVisible(false);
    container.addAt(this.veil, container.getIndex(b) + 1);
    container.add(this.front);
    scene.events.on(Phaser.Scenes.Events.UPDATE, this.onUpdate);
    container.once(Phaser.GameObjects.Events.DESTROY, () => this.destroy());
  }

  /** What it is showing now (review drivers, tests). */
  get pose(): WaterLook['pose'] { return this.look.pose; }
  /** The waterline as drawn now, 0..1 of the figure. */
  get cutNow(): number { return Math.min(1, this.level + this.sink); }

  /** docs/93 AQ9: it sinks away for good over `ms` (a withdrawal); the swirl is the board's. */
  sinkAway(ms: number): void {
    // born on its next frame, on the clock `update` is driven by (the loop's, not the scene's)
    this.sinking = { from: this.level, ms: Math.max(1, ms), born: Number.NaN };
  }

  destroy(): void {
    if (this.dead) return;
    this.dead = true;
    this.host.scene.events.off(Phaser.Scenes.Events.UPDATE, this.onUpdate);
  }

  private update(t: number): void {
    const { container, cell, size } = this.host;
    if (this.dead || !container.active) return;
    const dt = this.lastT ? Math.min(100, t - this.lastT) : 16;
    this.lastT = t;
    const fx = waterFxOf(this.host.scene);
    const flier = flies(this.host.gait);
    if (this.host.fallen() || (!flier && !fx?.any)) { this.rest(); return; }
    // the square under its feet (the container stands at the footprint's bottom centre)
    const gy = container.y + this.host.lift();
    const tile = { x: Math.round(container.x / cell - size / 2), y: Math.round(gy / cell - size) };
    const st = this.host.statuses();
    const now = waterLook(this.host.gait, fx ? bodyWater((p) => fx.at(p), tile, size) : [], { floundering: st.includes('Floundering'), surfaced: st.includes('Surfaced') });
    const moment = this.first ? undefined : waterMoment(this.look, now);
    const feet = { x: container.x, y: gy };
    if (moment && fx && container.visible) fx.moment(moment, feet, size);
    // a wake behind it as it goes through the water (not a flier over it)
    const wet = now.pose !== 'dry' && now.pose !== 'hover';
    const w = wakeStep(this.lastWake, feet, wet, cell, size);
    if (w.spawn && w.dir && fx && container.visible) fx.wake(feet, w.dir, size, now.water === 'deep');
    if (w.reset) this.lastWake = { ...feet };
    this.look = now;
    // the line eases toward the pose (a dive slides down, a surfacing rises); the first frame is where it is
    const target = now.cut;
    this.level = this.first ? target : this.level + Math.sign(target - this.level) * Math.min(Math.abs(target - this.level), dt * EASE_PER_MS);
    this.hoverK = this.first ? (flier ? 1 : 0) : this.hoverK + Math.sign((flier ? 1 : 0) - this.hoverK) * Math.min(Math.abs((flier ? 1 : 0) - this.hoverK), dt / 300);
    this.first = false;
    // the bob: floundering bobs and dips, a submerged back rolls gently, a swimmer rides the water
    const bob = now.pose === 'floundering' ? 0.06 * Math.sin(t / 210) + 0.03 * Math.sin(t / 97) : now.pose === 'submerged' ? 0.025 * Math.sin(t / 620) : now.pose === 'swimming' || now.pose === 'surfaced' ? 0.015 * Math.sin(t / 480) : 0;
    if (this.sinking) {
      if (Number.isNaN(this.sinking.born)) this.sinking.born = t;
      const k = Math.min(1, (t - this.sinking.born) / this.sinking.ms);
      this.sink = (1 - this.sinking.from) * k;
      if (k >= 1) container.setVisible(false);
    }
    const cut = Math.max(0, Math.min(0.97, this.level + (this.level > 0.01 ? bob : 0) + this.sink));
    const hover = this.hoverK * cell * HOVER * (size > 1 ? 0.7 : 1) * (1 + 0.15 * Math.sin(t / 520));
    this.cut(cut, hover);
    this.draw(t, cut, now);
  }

  /** Every body copy cut at `cut` of its height above the feet and dropped by as much; lifted by `hover` px. */
  private cut(cut: number, hover: number): void {
    const bodies = this.host.bodies();
    const b0 = this.host.body;
    const key = `${cut.toFixed(3)}|${hover.toFixed(1)}|${b0.flipX}|${b0.frame.name}|${b0.texture.key}`;
    const submerged = this.look.pose === 'submerged' ? Math.min(1, cut / CUT.submerged) : 0;
    if (key !== this.applied) {
      this.applied = key;
      for (const b of [...bodies, this.veil]) this.shape(b, cut, hover);
    }
    // the part of a submerged swimmer that shows is dark under the surface
    const v = this.veil;
    if (v.texture.key !== b0.texture.key || v.frame.name !== b0.frame.name) { v.setTexture(b0.texture.key, b0.frame.name); this.shape(v, cut, hover); }
    v.setPosition(b0.x, b0.y).setFlipX(b0.flipX).setAngle(b0.angle).setScale(b0.scaleX, b0.scaleY).setVisible(submerged > 0.01 && b0.visible).setAlpha(0.55 * submerged * b0.alpha);
  }

  private shape(b: Phaser.GameObjects.Image, cut: number, hover: number): void {
    const oy = this.host.originY();
    const fh = b.frame.height, fw = b.frame.width;
    const lift = hover / Math.max(1, fh * Math.abs(b.scaleY || 1));
    if (cut <= 0.001) {
      if (b.isCropped) b.setCrop();
      b.setData('fkCropH', undefined);
      b.setOrigin(b.originX, oy + lift);
      return;
    }
    // the figure above its feet is `fh × oy` texels; the line hides the bottom `cut` of it
    const keep = Math.max(1, Math.round(fh * oy * (1 - cut)));
    b.setCrop(0, 0, fw, keep);
    b.setData('fkCropH', keep);
    b.setOrigin(b.originX, keep / fh + lift);
  }

  /** The ripple ring at the waterline, a submerged body's shadow, a flier's shadow on the ground. */
  private draw(t: number, cut: number, look: WaterLook): void {
    const { cell, size } = this.host;
    const back = this.back, front = this.front;
    back.clear(); front.clear();
    const w = cell * size * 0.62, h = Math.max(3, w * 0.28);
    if (look.pose === 'hover') {
      // a soft shadow on the ground under a standing flier
      back.fillStyle(0x000000, 0.28 * this.hoverK).fillEllipse(0, -1, w * 1.05, h * 1.05);
      back.fillStyle(0x000000, 0.18 * this.hoverK).fillEllipse(0, -1, w * 0.7, h * 0.7);
      return;
    }
    if (cut <= 0.02 || look.pose === 'dry') return;
    const k = Math.min(1, cut / 0.2);
    if (look.pose === 'submerged') {
      // the body's dark shape under the water, and slow rings over it
      back.fillStyle(SHADOW, 0.38 * k).fillEllipse(0, -cell * size * 0.12, w * 1.15, h * 1.6);
    }
    // the waterline: its far half behind the figure, its near half in front
    back.lineStyle(1, RIPPLE, 0.55 * k);
    halfEllipse(back, w, h, true);
    front.lineStyle(size >= 2 ? 2 : 1, RIPPLE, 0.85 * k);
    halfEllipse(front, w, h, false);
    // a ripple spreading off it now and then (more often while it thrashes)
    const every = look.pose === 'floundering' ? 700 : 1600;
    const r = (t % every) / every;
    front.lineStyle(1, RIPPLE, 0.6 * (1 - r) * k).strokeEllipse(0, 0, w * (1 + 0.8 * r), h * (1 + 0.8 * r));
  }

  /** Out of the water (or it fell): uncut, unlifted, nothing drawn. */
  private rest(): void {
    if (this.applied === 'rest') return;
    this.applied = 'rest';
    this.look = { pose: 'dry', cut: 0 }; this.level = 0; this.hoverK = 0; this.sink = 0;
    for (const b of this.host.bodies()) this.shape(b, 0, 0);
    this.veil.setVisible(false);
    this.back.clear(); this.front.clear();
  }
}

/** The far (top) or near (bottom) half of an ellipse `w` × `h` round the feet. */
function halfEllipse(g: Phaser.GameObjects.Graphics, w: number, h: number, far: boolean): void {
  g.beginPath();
  for (let i = 0; i <= 16; i++) {
    const a = Math.PI * (i / 16) * (far ? -1 : 1);
    const x = Math.cos(a) * w / 2, y = Math.sin(a) * h / 2;
    if (i === 0) g.moveTo(x, y); else g.lineTo(x, y);
  }
  g.strokePath();
}
